import {Event as Event_V3} from '@wix/ambassador-events-v3-event/types'
import {addQueryParams, prependHTTP} from '@wix/panda-js-utils'
import {UrlMappingsKeys} from '@wix/url-mapper-utils'
import {
  BUILDER_EVENT_DETAILS_SECTION_ID,
  EVENTS_APP_ID,
  EVENT_DETAILS_SECTION_ID,
  EVENT_LIST_SECTION_ID,
  ExperimentNames,
  GROUPS_APP_ID,
  GROUPS_SECTION_ID,
  getExternalLink,
  getSlug,
  isEventDetailsPageHidden,
  isExternal,
} from '@wix/wix-events-commons-statics'
import {IWixAPI, Experiments} from '@wix/yoshi-flow-editor'
import {getSiteStructure, isPreview} from '../utils/wix-code-api'

export const navigateToDetailsPage = async (
  event: wix.events.Event | Event_V3,
  detailsPageEnabled: boolean,
  wixCodeApi: IWixAPI,
  experiments: Experiments,
) => {
  if (isExternal(event) && isEventDetailsPageHidden(event)) {
    return wixCodeApi.location.to(prependHTTP(getExternalLink(event)), {disableScrollToTop: true})
  }

  const {eventDetailsPageUrl, builderEventDetailsPageUrl} = await getEventDetailsPageUrl(wixCodeApi, experiments)
  const queryParams = getQueryParams(wixCodeApi)
  const url = builderEventDetailsPageUrl && detailsPageEnabled ? builderEventDetailsPageUrl : eventDetailsPageUrl
  const sectionId =
    builderEventDetailsPageUrl && detailsPageEnabled ? BUILDER_EVENT_DETAILS_SECTION_ID : EVENT_DETAILS_SECTION_ID
  const state = getDetailsTarget(event, detailsPageEnabled)

  if (wixCodeApi.location.to && url?.relativeUrl) {
    wixCodeApi.location.to(`${url.relativeUrl}/${state}${queryParams}`, {disableScrollToTop: true})
  } else {
    return wixCodeApi.location.navigateToSection({sectionId, state, queryParams: ''})
  }
}

export const getEventListPageUrl = async (wixCodeApi: IWixAPI) => {
  const [eventListPageUrl, eventListPageInstalled] = await Promise.all([
    wixCodeApi.site.getSectionUrl({
      appDefinitionId: EVENTS_APP_ID,
      sectionId: EVENT_LIST_SECTION_ID,
    }),
    wixCodeApi.site.isAppSectionInstalled({
      appDefinitionId: EVENTS_APP_ID,
      sectionId: EVENT_LIST_SECTION_ID,
    }),
  ])

  return eventListPageInstalled ? eventListPageUrl : undefined
}

export const getEventDetailsPageUrl = async (wixCodeApi: IWixAPI, experiments: Experiments) => {
  const navigationToBuilderEventDetailsPageEnabled = experiments.enabled(
    ExperimentNames.navigationToBuilderEventDetailsPage,
  )

  if (!navigationToBuilderEventDetailsPageEnabled) {
    const eventDetailsPageUrl = await wixCodeApi.site.getSectionUrl({
      appDefinitionId: EVENTS_APP_ID,
      sectionId: EVENT_DETAILS_SECTION_ID,
    })

    return {
      eventDetailsPageUrl,
    }
  }

  const [eventDetailsPageUrl, builderEventDetailsPageUrl, builderEventDetailsPageInstalled] = await Promise.all([
    wixCodeApi.site.getSectionUrl({appDefinitionId: EVENTS_APP_ID, sectionId: EVENT_DETAILS_SECTION_ID}),
    wixCodeApi.site.getSectionUrl(
      {
        appDefinitionId: EVENTS_APP_ID,
        sectionId: BUILDER_EVENT_DETAILS_SECTION_ID,
      },
      undefined,
      true,
    ),
    wixCodeApi.site.isAppSectionInstalled({
      appDefinitionId: EVENTS_APP_ID,
      sectionId: BUILDER_EVENT_DETAILS_SECTION_ID,
    }),
  ])

  return {
    eventDetailsPageUrl,
    builderEventDetailsPageUrl:
      builderEventDetailsPageInstalled && builderEventDetailsPageUrl.url ? builderEventDetailsPageUrl : undefined,
  }
}

export const getGroupPageUrl = async (wixCodeApi: IWixAPI) => {
  const url = await wixCodeApi.site.getSectionUrl({sectionId: GROUPS_SECTION_ID, appDefinitionId: GROUPS_APP_ID})
  return url?.relativeUrl
}

export const buildSiteGroupUrl = async (wixCodeApi: IWixAPI, slug: string) => {
  let url: string
  try {
    url = await wixCodeApi.location.buildCustomizedUrl?.(
      UrlMappingsKeys.GROUP,
      {
        groupId: slug,
        tabName: 'discussion',
      },
      {
        baseUrl: '',
      },
    )
  } catch (e) {
    console.warn('[Events:buildSiteGroupUrl] [location.buildCustomizedUrl] Failed')
  }
  if (!url) {
    const baseGroupsUrl = await getGroupPageUrl(wixCodeApi)
    url = `${baseGroupsUrl}/${slug}`
  }
  return url
}

export const navigateToGroup = async (slug: string, groupId: string, wixCodeApi: IWixAPI) => {
  if (wixCodeApi.location.to) {
    if (isPreview(wixCodeApi)) {
      const url = await getGroupPageUrl(wixCodeApi)
      wixCodeApi.location.to(`${url}?appSectionParams=${JSON.stringify({groupId})}`)
    } else {
      const url = await buildSiteGroupUrl(wixCodeApi, slug)
      wixCodeApi.location.to(`${url}?invite=true`)
    }
  }
}

export const getQueryParams = (wixCodeApi: IWixAPI) => addQueryParams('', wixCodeApi.location.query)

export const getDetailsTarget = (event: wix.events.Event | Event_V3, detailsPageEnabled: boolean) => {
  return `${getSlug(event)}${detailsPageEnabled ? '' : '/form'}`
}

export const getPage = async (wixCodeApi: IWixAPI, pageId: string) => {
  const {pages} = await getSiteStructure(wixCodeApi)
  return pages.find(page => page.id === pageId)
}

export const getHomePage = async (wixCodeApi: IWixAPI) => {
  const {pages} = await getSiteStructure(wixCodeApi)
  return pages.find(page => page.isHomePage) || pages[0]
}
